import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { X, Search, Image as ImageIcon, Loader2, Upload } from 'lucide-react';
import { uploadsAbsoluteUrl } from '../../../utils/uploadsBase';
import './MediaSelectModal.css';

const DEFAULT_API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Модалка: сетка всех медиафайлов, визуальный выбор по клику.
 */
export default function MediaSelectModal({
  open,
  onClose,
  token,
  apiUrl = DEFAULT_API,
  selectedId,
  onSelect,
  title = 'Выберите изображение',
  allowClear = true,
}) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const { data: mediaList = [], isLoading } = useQuery({
    queryKey: ['adminMediaList', token],
    queryFn: async () => {
      const r = await axios.get(`${apiUrl}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.data.media || [];
    },
    enabled: open && !!token,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mediaList;
    return mediaList.filter(
      (m) =>
        String(m.filename || '')
          .toLowerCase()
          .includes(q) ||
        String(m.url || '')
          .toLowerCase()
          .includes(q) ||
        String(m.id).includes(q),
    );
  }, [mediaList, search]);

  const handlePick = (id) => {
    onSelect(id);
    onClose();
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  return (
    <div
      className="media-select-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-select-modal-title"
      onMouseDown={handleBackdrop}
    >
      <div className="media-select-modal" onMouseDown={(e) => e.stopPropagation()}>
        <header className="media-select-modal-header">
          <h2 id="media-select-modal-title">{title}</h2>
          <button
            type="button"
            className="media-select-modal-close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X size={22} />
          </button>
        </header>

        <div className="media-select-modal-toolbar">
          <div className="media-select-modal-search">
            <Search size={18} />
            <input
              type="search"
              placeholder="Поиск по имени или ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {allowClear && selectedId != null && (
            <button
              type="button"
              className="media-select-modal-clear-btn"
              onClick={() => {
                onSelect(null);
                onClose();
              }}
            >
              Снять выбор
            </button>
          )}
        </div>

        <div className="media-select-modal-body">
          {isLoading ? (
            <div className="media-select-modal-loading">
              <Loader2 className="spin" size={36} />
              <span>Загрузка…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="media-select-modal-empty">
              <Upload size={40} />
              <p>Нет файлов. Загрузите их в разделе «Медиатека».</p>
            </div>
          ) : (
            <div className="media-select-modal-grid">
              {filtered.map((item) => {
                const src = uploadsAbsoluteUrl(item.url, apiUrl);
                const isVideo = /\.(mp4|webm|mov)$/i.test(item.url || '');
                const active = selectedId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`media-select-tile${active ? ' is-active' : ''}`}
                    onClick={() => handlePick(item.id)}
                    title={item.filename || item.url}
                  >
                    <div className="media-select-tile-thumb">
                      {isVideo ? (
                        <video src={src} muted playsInline />
                      ) : (
                        <img src={src} alt="" loading="lazy" />
                      )}
                    </div>
                    <span className="media-select-tile-label">
                      {item.filename || item.url}
                    </span>
                    <span className="media-select-tile-id">#{item.id}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="media-select-modal-footer">
          <span className="media-select-modal-hint">
            <ImageIcon size={16} />
            Нажмите на превью, чтобы выбрать
          </span>
          <button type="button" className="media-select-modal-cancel" onClick={onClose}>
            Отмена
          </button>
        </footer>
      </div>
    </div>
  );
}
